import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import axios from 'axios';
import AppSheetMrtTable from './AppSheetMrtTable';
import ConfirmDialog from './ConfirmDialog';

const DEFAULT_PAGE_SIZE = 100;
const LOAD_ALL_BATCH_SIZE = 1000;
const LOAD_ALL_MAX_ROWS = 20000;

const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);

  return debounced;
};

const buildColumns = (fields = []) =>
  fields.map((field) => ({
    id: field.name,
    header: field.displayName || field.name,
    size: 180,
    accessorFn: (row) => row?.[field.name],
  }));

const AppSheetTableGrid = ({ table }) => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  const cursorMapRef = useRef({ 0: null });
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(table?.cacheRowCount || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [loadAllMode, setLoadAllMode] = useState(false);
  const [loadAllLoading, setLoadAllLoading] = useState(false);
  const [loadAllConfirmOpen, setLoadAllConfirmOpen] = useState(false);

  const columns = useMemo(() => buildColumns(table?.fields || []), [table]);
  const debouncedGlobalFilter = useDebouncedValue(globalFilter, 350);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 350);
  const debouncedSorting = useDebouncedValue(sorting, 150);
  const sanitizedFilters = useMemo(
    () =>
      (Array.isArray(debouncedColumnFilters) ? debouncedColumnFilters : [])
        .map((filter) => ({
          id: filter?.id,
          value: typeof filter?.value === 'number' || typeof filter?.value === 'string' ? String(filter.value) : '',
        }))
        .filter((filter) => filter.id && filter.value !== ''),
    [debouncedColumnFilters]
  );

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
    cursorMapRef.current = { 0: null };
    setRows([]);
    setRowCount(table?.cacheRowCount || 0);
    setError(null);
    setLoadAllMode(false);
    setLoadAllLoading(false);
  }, [table?.id, table?.cacheRowCount]);

  useEffect(() => {
    if (loadAllMode) return;
    cursorMapRef.current = { 0: null };
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [debouncedGlobalFilter, sanitizedFilters, debouncedSorting, table?.id, loadAllMode]);

  useEffect(() => {
    if (!table?.id || loadAllMode) return;
    const { pageIndex, pageSize } = pagination;
    const cursor = cursorMapRef.current[pageIndex];
    if (pageIndex > 0 && cursor === undefined) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setError('Please page sequentially to load cached rows.');
      return;
    }

    let active = true;
    const controller = new AbortController();
    const fetchRows = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.post(
          `/api/appsheet/tables/${encodeURIComponent(table.id)}/rows/query`,
          {
            limit: pageSize,
            cursorId: cursor?.id ?? undefined,
            cursorGenerationId: cursor?.generationId,
            search: debouncedGlobalFilter,
            filters: sanitizedFilters,
            sort: debouncedSorting,
            includeTotal: true,
          },
          { signal: controller.signal }
        );
        if (!active) return;
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        if (data?.totalRows !== undefined && data?.totalRows !== null) {
          setRowCount(data.totalRows);
        }
        if (data?.nextCursor?.id !== undefined) {
          cursorMapRef.current = {
            ...cursorMapRef.current,
            [pageIndex + 1]: data.nextCursor,
          };
        }
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load AppSheet rows.');
      } finally {
        if (active) setLoading(false);
      }
    };

    void fetchRows();
    return () => {
      active = false;
      controller.abort();
    };
  }, [table?.id, pagination, debouncedGlobalFilter, sanitizedFilters, debouncedSorting, loadAllMode]);

  const handleLoadAllRows = async () => {
    if (!table?.id || loadAllLoading) return;
    const total = Number.isFinite(rowCount) ? rowCount : null;
    if (total && total > LOAD_ALL_MAX_ROWS) {
      setError(`Load-all is capped at ${LOAD_ALL_MAX_ROWS.toLocaleString()} rows. Current filters return ${total.toLocaleString()} rows.`);
      return;
    }
    setLoadAllConfirmOpen(true);
  };

  const confirmLoadAllRows = async () => {
    if (!table?.id || loadAllLoading) return;
    setLoadAllConfirmOpen(false);
    setLoadAllLoading(true);
    setError(null);
    try {
      let cursor = null;
      const allRows = [];
      let hasMore = true;
      while (hasMore) {
        const { data } = await axios.post(
          `/api/appsheet/tables/${encodeURIComponent(table.id)}/rows/query`,
          {
            limit: LOAD_ALL_BATCH_SIZE,
            cursorId: cursor?.id ?? undefined,
            cursorGenerationId: cursor?.generationId,
            search: debouncedGlobalFilter,
            filters: sanitizedFilters,
            sort: debouncedSorting,
            includeTotal: false,
          }
        );
        const nextRows = Array.isArray(data?.rows) ? data.rows : [];
        allRows.push(...nextRows);
        if (!data?.nextCursor || nextRows.length === 0) break;
        cursor = data.nextCursor;
        hasMore = Boolean(cursor);
      }
      setRows(allRows);
      setRowCount(allRows.length);
      setLoadAllMode(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load all rows.');
    } finally {
      setLoadAllLoading(false);
    }
  };

  const handleReturnToPaged = () => {
    setLoadAllMode(false);
    cursorMapRef.current = { 0: null };
    setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  };

  if (!table) {
    return <Alert severity="info">Select an AppSheet table to view cached rows.</Alert>;
  }

  if (!columns.length) {
    return <Alert severity="warning">No fields are configured for this AppSheet table.</Alert>;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {table.name} (cached rows)
      </Typography>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <AppSheetMrtTable
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        isLoading={loading}
        pagination={pagination}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater(pagination) : updater;
          if (next.pageSize !== pagination.pageSize) {
            cursorMapRef.current = { 0: null };
            setPagination({ pageIndex: 0, pageSize: next.pageSize });
            return;
          }
          setPagination(next);
        }}
        title={`${table.name} cached rows`}
        manualPagination={!loadAllMode}
        manualFiltering={!loadAllMode}
        manualSorting={!loadAllMode}
        onGlobalFilterChange={setGlobalFilter}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        toolbarActions={
          loadAllMode ? (
            <Button size="small" variant="contained" onClick={handleReturnToPaged}>
              Return to Paged
            </Button>
          ) : (
            <Button size="small" variant="contained" onClick={handleLoadAllRows} disabled={loadAllLoading || loading}>
              {loadAllLoading ? 'Loading…' : 'Load All Rows'}
            </Button>
          )
        }
      />
      <ConfirmDialog
        open={loadAllConfirmOpen}
        title="Load all rows?"
        description={`Load all ${Number.isFinite(rowCount) ? rowCount.toLocaleString() : ''} matching rows into this browser tab. Large result sets may be slower to sort and filter.`}
        confirmLabel="Load rows"
        onConfirm={confirmLoadAllRows}
        onCancel={() => setLoadAllConfirmOpen(false)}
        loading={loadAllLoading}
      />
    </Box>
  );
};

export default AppSheetTableGrid;
